import { Close } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';
import { Button, Icon } from 'vet-bones/design-system/components';

import { TEST_IDS } from 'src/constants/testIds';

import styles from 'src/components/drawer/drawer.module.scss';

export enum DrawerPosition {
  LEFT = 'left',
  RIGHT = 'right',
  TOP = 'top',
  BOTTOM = 'bottom',
}

export enum DrawerSize {
  MD = 'md',
  FULL = 'full',
}

export type DrawerProps = {
  autoFocus?: boolean;
  canEscapeKeyClose?: boolean;
  canOutsideClickClose?: boolean;
  children: React.ReactNode;
  className?: string;
  enforceFocus?: boolean;
  hasBackdrop?: boolean;
  isCloseButtonShown?: boolean;
  isOpen: boolean;
  onClose: () => void;
  position?: DrawerPosition;
  size?: DrawerSize;
  title?: React.ReactNode;
};

export const Drawer: React.VFC<DrawerProps> = ({
  autoFocus = true,
  canEscapeKeyClose = true,
  canOutsideClickClose = true,
  children,
  className,
  enforceFocus = true,
  hasBackdrop = true,
  isCloseButtonShown = true,
  isOpen,
  onClose,
  position = DrawerPosition.RIGHT,
  size = DrawerSize.MD,
  title,
}) => {
  const drawerRef = React.useRef<HTMLDivElement>(null);

  // Handle escape key
  React.useEffect(() => {
    if (!isOpen || !canEscapeKeyClose) {
      return undefined;
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, canEscapeKeyClose, onClose]);

  // Handle focus management
  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    if (autoFocus && drawerRef.current) {
      const focusableElement = drawerRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusableElement?.focus();
    }

    if (!enforceFocus) {
      return undefined;
    }

    const handleFocusOut = (e: FocusEvent) => {
      if (
        drawerRef.current &&
        e.target instanceof Node &&
        !drawerRef.current.contains(e.target)
      ) {
        drawerRef.current.focus();
      }
    };

    document.addEventListener('focusout', handleFocusOut);
    return () => {
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [isOpen, autoFocus, enforceFocus]);

  // Prevent body scroll when drawer is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = () => {
    if (canOutsideClickClose) {
      onClose();
    }
  };

  return (
    <>
      {hasBackdrop ? (
        <div
          className={styles.backdrop}
          data-testid={TEST_IDS.drawer.backdrop}
          onClick={handleBackdropClick}
          role="presentation"
        />
      ) : null}
      <div
        ref={drawerRef}
        aria-modal="true"
        className={classNames(
          styles.drawer,
          position === DrawerPosition.LEFT && styles.left,
          position === DrawerPosition.RIGHT && styles.right,
          position === DrawerPosition.TOP && styles.top,
          position === DrawerPosition.BOTTOM && styles.bottom,
          size === DrawerSize.MD && styles.md,
          size === DrawerSize.FULL && styles.full,
          className
        )}
        role="dialog"
        tabIndex={-1}
      >
        {title ? (
          <div className={styles.header} data-testid={TEST_IDS.drawer.header}>
            <div className={styles.headerContent}>{title}</div>
            {isCloseButtonShown ? (
              <Button
                className={styles.closeButton}
                onClick={onClose}
                size="xs"
                variant="secondary-solid-gray"
              >
                <Icon IconComponent={Close} size="sm" />
              </Button>
            ) : null}
          </div>
        ) : null}
        {children}
      </div>
    </>
  );
};
